/**
 * Announcements API Service
 *
 * Handles all API calls for system announcements.
 * Endpoints: /companies/{id}/groups/{grid}/company-users/announcements
 */

import axios from 'axios';
import { Auth } from 'aws-amplify';
import { API_LOCATION } from '@/api-config';
import { getCached } from '@/util/groupDataCache';

async function getAuthToken() {
  const session = await Auth.currentSession();
  return session.idToken.jwtToken;
}

function getCompanyId() {
  return localStorage.getItem('companyId');
}

function getCompanyGroupId() {
  const group = JSON.parse(localStorage.getItem('group') || '{}');
  return group.id;
}

function buildBaseUrl() {
  const companyId = getCompanyId();
  return `${API_LOCATION}companies/${companyId}/announcements`;
}

export const announcementsApi = {
  /**
   * Get announcements with optional filters
   * @param {Object} options - Filter options
   * @param {string} options.status - 'all', 'unread', or 'read'
   * @param {boolean} options.includeExpired - Include past announcements
   * @param {number} options.limit - Results per page
   * @param {number} options.offset - Pagination offset
   */
  async getAnnouncements(options = {}) {
    const token = await getAuthToken();
    const params = new URLSearchParams();

    if (options.status) params.append('status', options.status);
    if (options.includeExpired) params.append('include_expired', 'true');
    if (options.sort) params.append('sort', options.sort);
    if (options.limit) params.append('limit', options.limit);
    if (options.offset) params.append('offset', options.offset);

    const queryString = params.toString();
    const url = `${buildBaseUrl()}${queryString ? '?' + queryString : ''}`;

    return axios.get(url, {
      headers: { Authorization: token }
    });
  },

  /**
   * Get unread announcement count for badge
   */
  async getUnreadCount() {
    // Cached and shared by every bell on the page; mark-as-read writes evict it
    // through the axios interceptor. Keeps the { data } shape callers read.
    const data = await getCached('announcements-unread', async () => {
      const token = await getAuthToken();
      const url = `${buildBaseUrl()}/unread-count`;
      const res = await axios.get(url, {
        headers: { Authorization: token }
      });
      return res.data;
    });
    return { data };
  },

  /**
   * Mark a single announcement as read
   * @param {number} announcementId - Announcement ID
   * @param {boolean} dismissed - Whether to also dismiss the announcement
   */
  async markAsRead(announcementId, dismissed = false) {
    const token = await getAuthToken();
    const url = `${buildBaseUrl()}/${announcementId}`;

    return axios.put(url, { dismissed }, {
      headers: { Authorization: token }
    });
  },

  /**
   * Mark all visible announcements as read
   */
  async markAllAsRead() {
    const token = await getAuthToken();
    const url = buildBaseUrl();

    return axios.put(url, {}, {
      headers: { Authorization: token }
    });
  }
};

export default announcementsApi;
